/**
 * JobFormModal.jsx
 * Path: frontend/src/components/JobFormModal.jsx
 * Description: Modal tworzenia i edycji zlecenia.
 * Related: pages/CalendarPage.jsx, pages/JobDetailPage.jsx
 */

import { useEffect, useState } from "react";
import { fromDateInputValue, toDateInputValue } from "../utils/date";

const RESERVATION_COLOR_OPTIONS = [
  { value: "#F0542C", label: "Pomarańczowy" },
  { value: "#0F5F8E", label: "Niebieski" },
  { value: "#0F6A3C", label: "Zielony" },
  { value: "#6C3F8F", label: "Fioletowy" },
  { value: "#8D4F0F", label: "Bursztynowy" },
  { value: "#7A2B4E", label: "Malinowy" },
];

function buildFallbackTitle(seedDate) {
  const date = seedDate ? new Date(`${seedDate}T12:00:00`) : new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `Zlecenie ${month}/${date.getFullYear()}`;
}

function buildInitialState(job, seedDate, defaultTitle) {
  const startSeed = seedDate ? `${seedDate}T08:00` : toDateInputValue(new Date());
  const endSeed = seedDate ? `${seedDate}T16:00` : toDateInputValue(new Date(Date.now() + 2 * 60 * 60 * 1000));
  return {
    title: job?.title || defaultTitle || buildFallbackTitle(seedDate),
    customerName: job?.customerName || "",
    customerPhone: job?.customerPhone || "",
    address: job?.address || "",
    color: job?.color || "",
    scope: job?.scope || "",
    startAt: job?.startAt ? toDateInputValue(job.startAt) : startSeed,
    endAt: job?.endAt ? toDateInputValue(job.endAt) : endSeed,
    assignedUserIds: Array.isArray(job?.assignedUserIds) ? job.assignedUserIds : [],
  };
}

function formatGpsAddress(payload = {}) {
  const address = payload.address && typeof payload.address === "object" ? payload.address : {};
  const street = [address.road, address.house_number].filter(Boolean).join(" ");
  const locality = [address.postcode, address.city || address.town || address.village || address.hamlet].filter(Boolean).join(" ");
  const region = [address.state].filter(Boolean).join(" ");
  const parts = [street, locality, region].filter(Boolean);
  return parts.join(", ") || payload.display_name || "";
}

export default function JobFormModal({ job, seedDate, defaultTitle, users, onClose, onSave, saving }) {
  const workerUsers = users.filter((user) => String(user.role || "").toLowerCase() === "worker");
  const [form, setForm] = useState(() => buildInitialState(job, seedDate, defaultTitle));
  const [error, setError] = useState("");
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsError, setGpsError] = useState("");

  useEffect(() => {
    setForm(buildInitialState(job, seedDate, defaultTitle));
    setError("");
    setGpsError("");
  }, [defaultTitle, job, seedDate]);

  function handleChange(event) {
    const { name, value } = event.target;
    if (name === "customerPhone") {
      setForm((current) => ({ ...current, customerPhone: value.replace(/\D+/g, "").slice(0, 9) }));
      return;
    }
    setForm((current) => ({ ...current, [name]: value }));
  }

  function handleUserToggle(userId) {
    setForm((current) => {
      const exists = current.assignedUserIds.includes(userId);
      return {
        ...current,
        assignedUserIds: exists
          ? current.assignedUserIds.filter((item) => item !== userId)
          : [...current.assignedUserIds, userId],
      };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setGpsError("");

    const phoneDigits = String(form.customerPhone || "").replace(/\D+/g, "");
    if (phoneDigits && !/^\d{9}$/.test(phoneDigits)) {
      setError("Telefon musi mieć dokładnie 9 cyfr.");
      return;
    }

    try {
      await onSave({
        ...form,
        title: String(form.title || "").trim(),
        customerPhone: phoneDigits,
        startAt: fromDateInputValue(form.startAt),
        endAt: fromDateInputValue(form.endAt),
      });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Nie udało się zapisać zlecenia.");
    }
  }

  async function handleLocateAddress() {
    if (!navigator.geolocation) {
      setGpsError("To urządzenie nie udostępnia GPS w przeglądarce.");
      return;
    }

    setGpsLoading(true);
    setGpsError("");

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 30000,
        });
      });

      const latitude = position.coords?.latitude;
      const longitude = position.coords?.longitude;
      if (!latitude || !longitude) {
        throw new Error("Nie udało się odczytać pozycji GPS.");
      }

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&accept-language=pl`,
        { headers: { Accept: "application/json" } }
      );

      if (!response.ok) {
        throw new Error("Nie udało się pobrać adresu dla tej lokalizacji.");
      }

      const payload = await response.json();
      const resolvedAddress = formatGpsAddress(payload) || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
      setForm((current) => ({ ...current, address: resolvedAddress }));
    } catch (err) {
      if (err?.code === 1) {
        setGpsError("Brak zgody na dostęp do lokalizacji.");
      } else if (err?.code === 2) {
        setGpsError("Nie udało się ustalić lokalizacji telefonu.");
      } else if (err?.code === 3) {
        setGpsError("Odczyt lokalizacji przekroczył czas oczekiwania.");
      } else {
        setGpsError(err?.message || "Nie udało się pobrać adresu z GPS.");
      }
    } finally {
      setGpsLoading(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div className="modal-card" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h2>{job ? "Edytuj zlecenie" : "Nowa rezerwacja"}</h2>
          <button type="button" className="ghost-button" onClick={onClose}>
            Zamknij
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <label>
            <span>Tytuł</span>
            <input name="title" value={form.title} onChange={handleChange} placeholder={defaultTitle || buildFallbackTitle(seedDate)} />
          </label>

          <div className="form-grid">
            <label>
              <span>Klient</span>
              <input name="customerName" value={form.customerName} onChange={handleChange} />
            </label>
            <label>
              <span>Telefon</span>
              <input
                name="customerPhone"
                value={form.customerPhone}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={9}
                pattern="[0-9]{9}"
                placeholder="123456789"
              />
            </label>
          </div>

          <div className="field-stack">
            <label>
              <span>Adres</span>
              <input name="address" value={form.address} onChange={handleChange} />
            </label>
            <div className="field-inline-actions">
              <button type="button" className="ghost-button" onClick={handleLocateAddress} disabled={gpsLoading}>
                {gpsLoading ? "Lokalizowanie..." : "Pobierz z GPS"}
              </button>
              {gpsError ? <span className="helper-text helper-text-error">{gpsError}</span> : null}
            </div>
          </div>

          <div className="form-grid">
            <label>
              <span>Start</span>
              <input type="datetime-local" name="startAt" value={form.startAt} onChange={handleChange} required />
            </label>
            <label>
              <span>Koniec</span>
              <input type="datetime-local" name="endAt" value={form.endAt} onChange={handleChange} required />
            </label>
          </div>

          <div className="field-stack">
            <span>Kolor rezerwacji</span>
            <div className="color-palette" role="group" aria-label="Wybór koloru rezerwacji">
              <button
                type="button"
                className={`color-swatch-auto ${!form.color ? "color-swatch-selected" : ""}`}
                onClick={() => setForm((current) => ({ ...current, color: "" }))}
              >
                Auto
              </button>
              {RESERVATION_COLOR_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`color-swatch ${form.color === option.value ? "color-swatch-selected" : ""}`}
                  style={{ background: option.value }}
                  onClick={() => setForm((current) => ({ ...current, color: option.value }))}
                  title={option.label}
                  aria-label={option.label}
                />
              ))}
            </div>
          </div>

          <label>
            <span>Zakres prac</span>
            <textarea name="scope" value={form.scope} onChange={handleChange} rows={6} required />
          </label>

          <fieldset className="checkbox-fieldset">
            <legend>Ekipa</legend>
            <div className="checkbox-grid">
              {workerUsers.map((user) => (
                <label key={user.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={form.assignedUserIds.includes(user.id)}
                    onChange={() => handleUserToggle(user.id)}
                  />
                  <span>{user.name || user.email}</span>
                </label>
              ))}
              {workerUsers.length === 0 ? <div className="empty-state">Brak aktywnych pracowników z rolą worker.</div> : null}
            </div>
          </fieldset>

          {error ? <div className="form-error">{error}</div> : null}

          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={onClose}>
              Anuluj
            </button>
            <button type="submit" className="primary-button" disabled={saving}>
              {saving ? "Zapisywanie..." : job ? "Zapisz zmiany" : "Utwórz zlecenie"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
